import { useEffect, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { popOnboardingConfetti } from '@confluence/onboarding-helpers/entry-points/popOnboardingConfetti.ts';

import { useEntryPointDetection } from './useEntryPointDetection.tsx';
import { useOnboardingRovoConfig, type OnboardingRovoConfig } from './useOnboardingRovoConfig.tsx';

/**
 * Detects the `onboardingEntryPoint` URL param set during the onboarding redirect
 * and fires a `chat-new` publish event to open the Rovo mini-modal once the
 * AISmartButton is ready (`isMiniModalAvailable === true`).
 *
 * This is a one-shot action per page load — `hasFiredRef` prevents re-firing if
 * `isMiniModalAvailable` toggles after the event has been published.
 *
 * Intended for use in Rovo config providers (EditPage, ViewPage, Whiteboard) that
 * need to auto-open the mini-modal chat when a Founding Admin lands after onboarding.
 *
 * Returns the `OnboardingRovoConfig` when an entry point is active, or `null` when
 * no entry point was detected.
 */
export function useOnboardingEntryPointPublish(): OnboardingRovoConfig | null {
	const entryPoint = useEntryPointDetection();
	const publish = usePublish('ai-mate');
	const { isMiniModalAvailable } = useRovoExperienceAPIStore();
	const hasFiredRef = useRef(false);

	const rovoConfig = useOnboardingRovoConfig({
		skip: entryPoint === null,
		skippedAifc: entryPoint === 'cc-onboarding-skip' || entryPoint === 'cc-onboarding-xflow-skip',
	});

	// Fire the publish event once the entry point is detected, the mini-modal is available,
	// and the custom actions we plan to show have loaded.
	// hasFiredRef ensures this only happens once per page load.
	useEffect(() => {
		if (!entryPoint || !isMiniModalAvailable || hasFiredRef.current || rovoConfig === null) {
			return;
		}
		hasFiredRef.current = true;
		publish({
			type: 'chat-new',
			openChatMode: 'mini-modal',
			data: {
				dialogues: [],
			},
			source: entryPoint,
		});
		void popOnboardingConfetti();
	}, [entryPoint, isMiniModalAvailable, publish, rovoConfig]);

	return rovoConfig;
}
